"use client"

import React, { useCallback, useEffect, useLayoutEffect, useState, useRef, useMemo } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { ChevronLeft } from "lucide-react"
import { useTranslations } from "next-intl"
import { CreationTypeStep } from "./listing-creation/steps/creation-type-step"
// Listing photos are picked, not edited. The former `PhotosStep` mounted the
// 9:16 story editor, whose export fitted every photo INTO a 1080x1920 frame and
// baked a blurred filler into the leftover — those bars ended up inside the
// stored file (11 of 12 sampled prod photos were 1080x1920 with bars in the
// JPEG). `PhotosUploadStep` uploads the seller's own photo through the normal
// `getNormalizedImageFile` path instead: HEIC->JPEG, compressed, own aspect.
import { PhotosUploadStep } from "./listing-creation/steps/photos-upload-step"
import { ArrangePhotosStep } from "./listing-creation/steps/arrange-photos-step"
import { ProductTypeStep } from "./listing-creation/steps/product-type-step"
import { CategoryStep } from "./listing-creation/steps/category-step"
import { DetailsStep } from "./listing-creation/steps/details-step"
import { ConditionStep } from "./listing-creation/steps/condition-step"
import { CardCategoryStep } from "./listing-creation/steps/card-category-step"
import { CardDetailsStep } from "./listing-creation/steps/card-details-step"
import { CardConditionStep } from "./listing-creation/steps/card-condition-step"
import { CardReviewStep } from "./listing-creation/steps/card-review-step"
import { ComicCategoryStep } from "./listing-creation/steps/comic-category-step"
import { ComicDetailsStep } from "./listing-creation/steps/comic-details-step"
import { ComicConditionStep } from "./listing-creation/steps/comic-condition-step"
import { ComicReviewStep } from "./listing-creation/steps/comic-review-step"
import { ShippingStep } from "./listing-creation/steps/shipping-step"
import { ScheduleStep } from "./listing-creation/steps/schedule-step"
import { ReviewStep } from "./listing-creation/steps/review-step"
import type { ListingCategory, ListingData } from "@/types/listing"
import { DEFAULT_CURRENCY } from "@/lib/currencies"
import { cn } from "@/lib/utils"

interface StepConfig {
  name: string
  component: React.ComponentType<any>
  titleKey: string
}

const legoStepsConfig: StepConfig[] = [
  { name: "Type", component: CreationTypeStep, titleKey: "create" },
  { name: "Photos", component: PhotosUploadStep, titleKey: "photos" },
  { name: "ArrangePhotos", component: ArrangePhotosStep, titleKey: "arrangePhotos" },
  { name: "ProductType", component: ProductTypeStep, titleKey: "productType" },
  { name: "Category", component: CategoryStep, titleKey: "category" },
  { name: "Details", component: DetailsStep, titleKey: "cardDetails" },
  { name: "Condition", component: ConditionStep, titleKey: "conditionGrading" },
  // ADR_LISTING_SHIP_FROM_ADDRESS Phase 3 — Location step removed; origin is
  // pinned via ShipFromAddressPicker inside ShippingStep.
  { name: "Shipping", component: ShippingStep, titleKey: "shippingOptions" },
  { name: "Schedule", component: ScheduleStep, titleKey: "schedule" },
  { name: "Review", component: ReviewStep, titleKey: "reviewPublish" },
]

const cardStepsConfig: StepConfig[] = [
  { name: "Type", component: CreationTypeStep, titleKey: "create" },
  { name: "Photos", component: PhotosUploadStep, titleKey: "photos" },
  { name: "ArrangePhotos", component: ArrangePhotosStep, titleKey: "arrangePhotos" },
  { name: "ProductType", component: ProductTypeStep, titleKey: "productType" },
  { name: "Category", component: CardCategoryStep, titleKey: "cardCategory" },
  { name: "Details", component: CardDetailsStep, titleKey: "cardDetails" },
  { name: "Condition", component: CardConditionStep, titleKey: "conditionGrading" },
  // ADR_LISTING_SHIP_FROM_ADDRESS Phase 3 — Location step removed.
  { name: "Shipping", component: ShippingStep, titleKey: "shippingOptions" },
  { name: "Schedule", component: ScheduleStep, titleKey: "schedule" },
  { name: "Review", component: CardReviewStep, titleKey: "reviewPublish" },
]

export const listingStepsConfig = legoStepsConfig

const comicStepsConfig: StepConfig[] = [
  { name: "Type", component: CreationTypeStep, titleKey: "create" },
  { name: "Photos", component: PhotosUploadStep, titleKey: "photos" },
  { name: "ArrangePhotos", component: ArrangePhotosStep, titleKey: "arrangePhotos" },
  { name: "ProductType", component: ProductTypeStep, titleKey: "productType" },
  { name: "Category", component: ComicCategoryStep, titleKey: "comicCategory" },
  { name: "Details", component: ComicDetailsStep, titleKey: "comicDetails" },
  { name: "Condition", component: ComicConditionStep, titleKey: "conditionGrading" },
  { name: "Shipping", component: ShippingStep, titleKey: "shippingOptions" },
  { name: "Schedule", component: ScheduleStep, titleKey: "schedule" },
  { name: "Review", component: ComicReviewStep, titleKey: "reviewPublish" },
]

// Record keyed by the canonical tuple — adding a category without a steps
// config is a compile error (Wave 0, NEW_CATEGORIES_PLAN §3.1).
const stepsConfigByCategory: Record<ListingCategory, StepConfig[]> = {
  lego: legoStepsConfig,
  trading_card: cardStepsConfig,
  comic: comicStepsConfig,
}

function getStepsForCategory(category?: ListingCategory): StepConfig[] {
  return stepsConfigByCategory[category ?? "lego"]
}

interface ListingCreationStepsProps {
  data: ListingData
  onDataChangeAction: (patch: Partial<ListingData>) => void
  onBackToSelectionAction: () => void
  onCompleteAction: () => void
  isMobile: boolean
  onHeaderChange?: (info: {
    title?: string
    description?: string
    onBack?: () => void
    progress?: {
      steps: string[]
      currentStep: number
      onStepClick: (index: number) => void
    }
  }) => void
  onFooterActionChange?: (action: (() => Promise<void>) | null) => void
  onFooterValidChange?: (isValid: boolean) => void
  onFooterMetaChange?: (meta: { isLastStep: boolean; isSubmitting: boolean }) => void
  onStepNameChange?: (name: string) => void
  fullViewportStep?: boolean
  compact?: boolean
  controlledStep?: number
  onStepChange?: (step: number) => void
  /**
   * Seed for the UNCONTROLLED step (draft resume). Deliberately not the
   * controlled API: `controlledStep` without full control breaks stepping and
   * hides the progress indicator. Clamped to the category's step range.
   */
  initialStep?: number
  /** Notifies the parent of every visited step — persisted into drafts. */
  onStepVisit?: (step: number) => void
}

export function ListingCreationSteps({
  data,
  onDataChangeAction,
  onBackToSelectionAction,
  onCompleteAction,
  isMobile,
  onHeaderChange,
  onFooterActionChange,
  onFooterValidChange,
  onFooterMetaChange,
  onStepNameChange,
  fullViewportStep = false,
  compact = false,
  controlledStep,
  onStepChange,
  initialStep,
  onStepVisit,
}: ListingCreationStepsProps) {
  const tStep = useTranslations("cardCreation.stepTitles")
  const [internalStep, setInternalStep] = useState(() => {
    const max = getStepsForCategory(data.listingCategory).length - 1
    return Math.min(Math.max(initialStep ?? 0, 0), max)
  })
  const step = controlledStep !== undefined ? controlledStep : internalStep
  const [visibleStep, setVisibleStep] = useState(() => step)
  const stepTargetRef = useRef(step)
  stepTargetRef.current = step
  const setStep = useCallback(
    (updater: number | ((prev: number) => number)) => {
      const next = typeof updater === "function" ? updater(step) : updater
      if (onStepChange) onStepChange(next)
      else setInternalStep(next)
    },
    [step, onStepChange]
  )
  const [isStepValid, setIsStepValid] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const steps = getStepsForCategory(data.listingCategory)
  const stepSaveRef = useRef<(() => Promise<void> | void) | null>(null)

  const updateListingData = useCallback(
    (patch: Partial<ListingData>) => {
      onDataChangeAction(patch)
    },
    [onDataChangeAction]
  )

  // Function to register the save handler for the current step
  const registerStepSave = useCallback((saveFunction: (() => Promise<void> | void) | null) => {
    stepSaveRef.current = saveFunction
  }, [])

  const nextStep = useCallback(() => {
    setStep((current) => {
      if (current < steps.length - 1) {
        if (navigator.vibrate) {
          navigator.vibrate(10)
        }
        return current + 1
      }
      return current
    })
  }, [setStep, steps.length])

  const prevStep = useCallback(() => {
    if (stepSaveRef.current) {
      stepSaveRef.current()
    }
    setStep((current) => {
      if (current > 0) {
        if (navigator.vibrate) {
          navigator.vibrate(10)
        }
        return current - 1
      }
      onBackToSelectionAction()
      return current
    })
  }, [onBackToSelectionAction, setStep])

  const handleSubmit = useCallback(async () => {
    if (navigator.vibrate) {
      navigator.vibrate([15, 30, 15])
    }
    onCompleteAction()
  }, [onCompleteAction])

  useEffect(() => {
    if (data.currency) return
    ;(async () => {
      try {
        const res = await fetch("/api/user/profile")
        if (!res.ok) return
        const profile = await res.json().catch(() => null)
        const currency = typeof profile?.currency === "string" && profile.currency ? profile.currency : DEFAULT_CURRENCY
        updateListingData({ currency })
      } catch {
        updateListingData({ currency: DEFAULT_CURRENCY })
      }
    })()
  }, [data.currency, updateListingData])

  useEffect(() => {
    onHeaderChange?.({
      title: tStep(steps[visibleStep]?.titleKey ?? "create"),
      onBack: visibleStep > 0 ? prevStep : undefined,
      ...(controlledStep === undefined && {
        progress: {
          steps: steps.map((s) => s.name),
          currentStep: visibleStep,
          onStepClick: (i: number) => setStep(i),
        },
      }),
    })
  }, [visibleStep, step, steps, onHeaderChange, prevStep, controlledStep, setStep, tStep])

  useLayoutEffect(() => {
    onStepNameChange?.(steps[visibleStep]?.name ?? "")
  }, [visibleStep, steps, onStepNameChange])

  useEffect(() => {
    onStepVisit?.(step)
  }, [step, onStepVisit])

  const latestStepActionRef = useRef<{
    step: number
    stepsLength: number
    handleSubmit: () => Promise<void>
    nextStep: () => void
  } | null>(null)
  latestStepActionRef.current = { step, stepsLength: steps.length, handleSubmit, nextStep }

  useEffect(() => {
    if (!onFooterActionChange) return
    const action = async () => {
      const ctx = latestStepActionRef.current
      if (!ctx) return
      const isLastStep = ctx.step === ctx.stepsLength - 1
      if (isLastStep) {
        try {
          setIsSubmitting(true)
          await (stepSaveRef.current
            ? Promise.resolve(stepSaveRef.current())
            : ctx.handleSubmit())
        } finally {
          setIsSubmitting(false)
        }
      } else {
        if (stepSaveRef.current) await Promise.resolve(stepSaveRef.current())
        ctx.nextStep()
      }
    }
    onFooterActionChange(action)
    return () => {
      onFooterActionChange(null)
      setIsStepValid(false)
    }
  }, [step, onFooterActionChange])

  useEffect(() => {
    onFooterMetaChange?.({ isLastStep: step === steps.length - 1, isSubmitting })
  }, [step, steps.length, isSubmitting, onFooterMetaChange])

  useEffect(() => {
    onFooterValidChange?.(isStepValid)
  }, [isStepValid, onFooterValidChange])

  return (
    <div
      className={
        fullViewportStep
          ? "flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col"
          : compact
            ? "pt-4 pb-2 min-h-0 w-full min-w-0 max-w-full"
            : "flex-1 min-h-0 w-full overflow-y-auto px-3 pt-4 pb-4 scrollbar-none [&::-webkit-scrollbar]:hidden"
      }
      style={(!fullViewportStep && !compact) ? { paddingBottom: "calc(6rem + env(safe-area-inset-bottom))" } : undefined}
    >
      <AnimatePresence
        mode="wait"
        onExitComplete={() => setVisibleStep(stepTargetRef.current)}
      >
        <motion.div
          key={step}
          className={
            fullViewportStep
              ? "flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col"
              : "w-full min-w-0 max-w-full"
          }
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          {React.createElement(steps[step].component, {
            data,
            updateDataAction: updateListingData,
            onNextAction: nextStep,
            onPrevAction: prevStep,
            onGoToStepAction: setStep,
            isLastStep: step === steps.length - 1,
            onSubmit: handleSubmit,
            hideButton: true,
            onValidationChange: setIsStepValid,
            onRegisterSave: registerStepSave,
            compact: true,
            isMobile,
            allowAuctionOption: true,
            fullViewport: fullViewportStep,
          })}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

ListingCreationSteps.stepMeta = listingStepsConfig.map((step) => ({
  titleKey: step.titleKey,
  name: step.name,
}))
